import type { FC } from 'react'

import { LoaderCircle, Table } from '@skytech/ui'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'
import terminationResources from '@/modules/subscriptions/termination/resources'

import { SUB_SERVICE_ORDER_STATUS_BY_ID } from '../constants'
import { SUB_SERVICE_ORDER_STATUS } from '../constants'

import queries from './queries'
import TerminationListItem from './TerminationListItem'
import type { Termination } from './types'

import './TerminationList.styles.scss'

const TerminationList: FC<{
  searchFilter: string
  statusFilter: number
}> = ({ statusFilter, searchFilter }) => {
  const translate = useTranslation()
  const { data: terminationList = [], isLoading } = useQuery(
    queries.terminationList(),
    terminationResources.getList
  )
  const stringMatcher = (item: Termination) => [
    item.name,
    item.employeeNumber,
    translate(`sub_service_order_status/${SUB_SERVICE_ORDER_STATUS_BY_ID[item.status]}`),
    translate(`termination_type/${item.type}`),
    item.phoneNo,
    item.employeeNumber,
    item.customerName,
    item.orgNo,
  ]

  const getFilteredList = () => {
    if (statusFilter === undefined || statusFilter === SUB_SERVICE_ORDER_STATUS.ALL)
      return terminationList.filter(getQueryFilter(searchFilter, stringMatcher))

    return terminationList.filter(
      termination =>
        termination.status === statusFilter &&
        getQueryFilter(searchFilter, stringMatcher)(termination)
    )
  }
  const { locale } = useAppContext()
  const filteredList = getFilteredList()

  const [{ querySort, querySortBy }, { handleSort }] =
    useSortParams<keyof (typeof filteredList)[number]>()

  const sortedList = getSortedList({ list: filteredList, querySort, querySortBy, locale })
  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList, 15)

  if (isLoading) return <LoaderCircle />

  if (visibleList.length === 0)
    return (
      <NoResults>
        <Translation id="No terminations found" />
      </NoResults>
    )

  return (
    <>
      <ResultCount count={filteredList.length} />
      <Table rowHeight="tall">
        <thead>
          <tr>
            <th> </th>
            <Th id="name" onClick={handleSort}>
              <Translation id="user" />
            </Th>
            <Th id="phoneNo" onClick={handleSort}>
              <Translation id="phone number" />
            </Th>
            <Th id="customerName" onClick={handleSort}>
              <Translation id="company" />
            </Th>

            <Th id="status" onClick={handleSort}>
              <Translation id="status" />
            </Th>
            <Th id="terminationDate" onClick={handleSort}>
              <Translation id="termination date" />
            </Th>
            <Th id="created" onClick={handleSort}>
              <Translation id="created" />
            </Th>
            <Th id="updated" onClick={handleSort}>
              <Translation id="updated" />
            </Th>

            <th />
          </tr>
        </thead>

        <tbody>
          {visibleList.map(item => (
            <TerminationListItem key={item.id} searchFilter={searchFilter} {...item} />
          ))}
        </tbody>
      </Table>
      <ListLoaderComponent />
    </>
  )
}

export default TerminationList
